<script setup lang="ts">
import { onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { apiPost } from '@/lib/http'
import { loadYandexMaps } from '@/lib/ymaps'

const asset = useStaticAsset()

const placemarkTypes = {
  n: 'map-marker-blue', // n? (teacher)
  a: 'map-marker-green', // ace
  t: 'map-marker-orange', // tutor
  m: 'map-marker-red' // methodist
}

type MapLocation = {
  id: number
  lat: number | string
  long: number | string
  type: keyof typeof placemarkTypes
}

const mapContainer = useTemplateRef<HTMLElement>('mapContainer')
const map = shallowRef<{ setCenter: (coords: number[]) => void; geoObjects: { add: (obj: unknown) => void } } | null>(
  null
)
interface TeacherInfo {
  id?: number
  name?: string
  address?: string
  marker?: string
  no_bio_unreg?: boolean
  avatar?: { image?: string; bio_link?: string }
  languages?: Array<{ code: string; name: string }>
  [key: string]: unknown
}

const teacher = ref({
  show: false,
  loading: false,
  errorText: '',
  info: {} as TeacherInfo
})

const loading = ref(true)
const loadingFailReason = ref('')
const placemarks: unknown[] = []

function resetTeacher() {
  teacher.value = {
    show: false,
    loading: false,
    errorText: '',
    info: {}
  }
}

async function showTeacherInfo(item: MapLocation) {
  map.value?.setCenter([Number(item.lat), Number(item.long)])

  teacher.value = {
    show: true,
    loading: true,
    errorText: '',
    info: {}
  }

  try {
    const { data, failed, message } = await apiPost<Record<string, unknown>>('/teacher/request_map_location_data', {
      location_id: item.id
    })
    if (failed) throw message

    teacher.value.info = {
      ...data,
      marker: placemarkTypes[item.type as keyof typeof placemarkTypes]
    }
  } catch (err) {
    console.error(err)

    let title = 'Не удалось загрузить информацию об учителе. Повторите попытку.'
    if (typeof err === 'string') title = err

    teacher.value.errorText = title
  }

  teacher.value.loading = false
}

function closeTeacherInfo() {
  resetTeacher()
}

onMounted(async () => {
  const ymaps = await loadYandexMaps()

  map.value = new ymaps.Map(mapContainer.value, {
    center: [55.751574, 37.573856], // Москва
    zoom: 10,
    controls: [
      new ymaps.control.GeolocationControl({
        options: { float: 'right' }
      }),
      new ymaps.control.ZoomControl({
        options: { position: { left: 'auto', right: 10, top: 108, bottom: 'auto' } }
      })
    ]
  })

  const clusterer = new ymaps.Clusterer({
    preset: 'islands#blueCircleIcon',
    zoomMargin: 64
  })

  try {
    const { data, failed, message } = await apiPost<MapLocation[]>('/teacher/request_map_locations')
    if (failed) throw message

    for (const item of data) {
      const placemark = new ymaps.Placemark(
        [Number(item.lat), Number(item.long)],
        {},
        {
          iconLayout: 'default#image',
          iconImageHref: asset(`img/${placemarkTypes[item.type]}.svg`),
          iconImageSize: [32, 32],
          iconImageOffset: [0, -24]
        }
      )

      // might be a performance issue when there's many placemarks
      placemark.events.add('mousedown', () => {
        showTeacherInfo(item)
      })

      clusterer.add(placemark)
      placemarks.push(placemark)
    }

    map.value.geoObjects.add(clusterer)
  } catch (err) {
    console.error(err)

    let title = 'Не удалось загрузить карту. Попробуйте обновить страницу.'
    if (typeof err === 'string') title = err

    loadingFailReason.value = title
  }

  loading.value = false
})
</script>

<template>
  <div class="public-teacher-map">
    <div ref="mapContainer" class="public-teacher-map__ymap" />

    <transition name="fade">
      <div v-if="loading || loadingFailReason" class="public-teacher-map__preloader">
        <transition-group name="slide-fade-y" class="public-teacher-map__transition">
          <div v-if="loadingFailReason" key="failed" class="public-teacher-map__preloader-card red-text">
            <v-icon class="mb-2" size="36">mdi-alert-circle</v-icon>
            {{ loadingFailReason }}
          </div>

          <div v-else key="loading" class="public-teacher-map__preloader-card blue-text">
            <v-icon class="mdi-spin mb-2" size="36">mdi-timer-sand</v-icon>
            Загрузка...
          </div>
        </transition-group>
      </div>
    </transition>

    <transition name="slide-fade-x-reverse">
      <div v-if="teacher.show" class="public-teacher-map__info">
        <v-link class="public-teacher-map__info-close-btn" @click="closeTeacherInfo">
          <v-icon>mdi-close</v-icon>
        </v-link>

        <div
          v-if="teacher.loading || teacher.errorText"
          class="public-teacher-map__info-preloader"
          :class="teacher.errorText ? 'red-text' : 'blue-text'"
        >
          <template v-if="teacher.errorText">
            <v-icon class="mb-2" size="36">mdi-alert-circle</v-icon>
            {{ teacher.errorText }}
          </template>
          <template v-else>
            <v-icon class="mdi-spin mb-2" size="36">mdi-timer-sand</v-icon>
            Загрузка...
          </template>
        </div>

        <template v-if="teacher.info.no_bio_unreg">
          <v-teacher-avatar
            :size="100"
            :avatar="{ image: asset('users/default_profile_image.png') }"
          ></v-teacher-avatar>

          <p class="mt-2">Визитка этого учителя доступна только зарегистрированным пользователям.</p>

          <p class="mt-2 flex-spacer">
            <v-link href="/users/login?student_login=true" mode="primary"> Зарегистрируйтесь или войдите </v-link>
            на сайт, чтобы увидеть его визитку.
          </p>

          <div class="d-flex align-center">
            <v-icon :key="teacher.info.marker" class="mr-1 public-teacher-map__info-marker">
              {{ teacher.info.marker }}
            </v-icon>

            {{ teacher.info.address }}
          </div>
        </template>

        <template v-else-if="teacher.info.id">
          <v-teacher-avatar :size="100" :avatar="teacher.info.avatar"></v-teacher-avatar>

          <h3 class="text-title-bold mt-2" v-text="teacher.info.name" />

          <div class="flex-spacer mt-1 d-flex flex-column">
            <span>Языки:</span>
            <ul class="public-teacher-map__info-list">
              <li v-for="lang in teacher.info.languages" :key="lang.code" v-text="lang.name" />
            </ul>
          </div>

          <div class="d-flex align-center mt-1">
            <v-icon :key="teacher.info.marker" class="mr-1 public-teacher-map__info-marker">
              {{ teacher.info.marker }}
            </v-icon>

            {{ teacher.info.address }}
          </div>

          <v-button class="mt-1" mode="primary filled block" :href="teacher.info.avatar?.bio_link" target="_blank">
            Открыть визитку
          </v-button>
        </template>
      </div>
    </transition>
  </div>
</template>

<style lang="scss">
.public-teacher-map {
  height: 400px;
  position: relative;
  overflow: hidden;

  border-radius: 8px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.1);

  &__ymap {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &__info {
    position: absolute;
    overflow-y: auto;

    left: 0;
    top: 0;
    bottom: 0;

    width: 300px;
    background: white;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.1);

    padding: 16px;

    @include mobile {
      width: 90%;
    }
  }

  &__preloader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    z-index: 2;
    background: rgba(white, 0.5);
    user-select: none;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__transition {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    width: min(90vw, 320px);
  }

  &__preloader-card {
    border-radius: 8px;
    padding: 16px 24px;
    background: white;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.1);

    text-align: center;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
  }

  &__info-preloader {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    text-align: center;
  }

  &__info-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  &__info-marker {
    flex: 0 0 24px;
  }

  &__info-list {
    list-style: disc !important;

    & > li {
      display: list-item;
      list-style: disc !important;
      margin-left: 20px;
    }
  }
}
</style>
